import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { LogOut, Building2, Mail, Key, Calendar } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { signOutAuth } from '../lib/supabaseAuth';
import { Member } from '../types';
import { requiresPasswordSetup } from '../lib/passwordSetup';
import { isPersonalLoginEnabled } from '../lib/personalLoginAccess';

export default function AccountPage() {
  const navigate = useNavigate();
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);
  const [authInfo, setAuthInfo] = useState<{ id: string; email: string | null } | null>(null);
  const [membershipError, setMembershipError] = useState<string | null>(null);
  const [showAdminDetails, setShowAdminDetails] = useState(false);

  const formatMembershipErrorJa = (msg: string) => {
    if (msg.includes('infinite recursion detected in policy for relation')) {
      return '権限設定（RLS）に不整合があります（clinic_users）。DBのポリシー修正が必要です。';
    }
    if (msg.includes('permission denied')) {
      return '権限がありません（RLSで拒否されました）。';
    }
    return '所属情報の取得に失敗しました。管理者に連絡してください。';
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        if (requiresPasswordSetup(data.user)) {
          navigate('/setup-password');
          return;
        }
        if (!isPersonalLoginEnabled(data.user)) {
          await signOutAuth();
          navigate('/login');
          return;
        }
        const userId = data.user?.id;
        if (!userId) {
          navigate('/login');
          return;
        }
        setAuthInfo({ id: userId, email: data.user?.email ?? null });

        // Prefer sending user into their clinic app if membership exists.
        const { data: cu, error: cuError } = await supabase
          .from('clinic_users')
          .select('clinic_id')
          .eq('auth_user_id', userId)
          .eq('is_active', true)
          .limit(1)
          .maybeSingle();
        if (cuError) {
          console.error('clinic_users lookup error:', cuError);
          setMembershipError(formatMembershipErrorJa(String(cuError.message || '')));
        }
        const clinicId = (cu as any)?.clinic_id as string | undefined;
        if (clinicId) {
          navigate(`/${clinicId}`);
          return;
        }

        // No membership yet: keep page empty (legacy behavior) but stop spinner.
        setMember(null);
      } catch (error) {
        console.error('Failed to load account:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleLogout = () => {
    (async () => {
      try {
        await signOutAuth();
      } catch {}
      navigate('/login');
    })();
  };

  const handleGoToClinicApp = () => {
    if (member) {
      navigate(`/${member.clinicId}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-teal-600">Handra</CardTitle>
            <CardDescription>アカウント</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-gray-700">
                ログインはできていますが、このユーザーはまだ医院に所属していません（または所属が無効化されています）。
              </p>
              <p className="text-sm text-gray-500">
                管理者が <code className="font-mono">clinic_users</code> の紐付けを行う必要があります。
              </p>

              {authInfo?.email && (
                <div className="rounded-md bg-white/70 p-3 text-sm text-gray-700">
                  <div>
                    <span className="text-gray-500">Email:</span>{' '}
                    <span className="font-mono">{authInfo.email}</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    管理者へはまずメールアドレスを伝えてください（通常、Auth UID の提示は不要です）。
                  </div>
                </div>
              )}

              {/* 管理者/サポート向けの詳細情報（通常ユーザーには不要なので折りたたみ） */}
              {(membershipError || authInfo?.id) && (
                <div className="rounded-md border border-gray-200 bg-white p-3">
                  <button
                    type="button"
                    onClick={() => setShowAdminDetails((v) => !v)}
                    className="w-full text-left text-sm font-semibold text-gray-800"
                  >
                    {showAdminDetails ? '管理者向け詳細を隠す' : '管理者向け詳細を表示'}
                  </button>
                  {showAdminDetails && (
                    <div className="mt-2 space-y-2 text-sm text-gray-700">
                      {authInfo?.id && (
                        <div>
                          <span className="text-gray-500">Auth UID:</span>{' '}
                          <span className="font-mono break-words">{authInfo.id}</span>
                        </div>
                      )}
                      {membershipError && (
                        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                          <div className="font-bold">所属情報の取得エラー</div>
                          <div className="font-mono break-words">{membershipError}</div>
                        </div>
                      )}
                      <div className="text-xs text-gray-500">
                        ※この情報は、<code className="font-mono">clinic_users</code> の紐付け作業やサポート対応向けです。
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-2 space-y-2">
                <Button variant="cta" onClick={handleLogout} className="w-full">
                  ログアウト
                </Button>
                <Button onClick={() => navigate('/login')} variant="outline" className="w-full">
                  ログイン画面へ
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 p-4">
      <div className="max-w-4xl mx-auto py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-teal-600">Handra</h1>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            ログアウト
          </Button>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* 会員情報カード */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                医院情報
              </CardTitle>
              <CardDescription>登録されている医院情報</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm text-gray-500">医院名</Label>
                <p className="text-lg font-semibold">{member.clinicName || '未設定'}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Clinic ID</Label>
                <p className="text-lg font-mono">{member.clinicId}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">プラン</Label>
                <p className="text-lg">{member.plan === 'basic' ? 'ベーシック' : member.plan}</p>
              </div>
            </CardContent>
          </Card>

          {/* アカウント情報カード */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                アカウント情報
              </CardTitle>
              <CardDescription>ログイン情報</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm text-gray-500">メールアドレス</Label>
                <p className="text-lg">{member.email}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">登録日</Label>
                <p className="text-lg">
                  {new Date(member.createdAt).toLocaleDateString('ja-JP')}
                </p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">ステータス</Label>
                <p className="text-lg">
                  {member.isActive ? (
                    <span className="text-green-600">有効</span>
                  ) : (
                    <span className="text-red-600">無効</span>
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* アクションボタン */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              現場アプリへのアクセス
            </CardTitle>
            <CardDescription>スタッフ向けのPINログイン画面へ</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="cta" onClick={handleGoToClinicApp} className="w-full" size="lg">
              <Calendar className="w-5 h-5 mr-2" />
              現場アプリを開く
            </Button>
            <p className="text-sm text-gray-500 mt-2 text-center">
              URL: /{member.clinicId}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

